<!-- EntityPlanning.vue -->
<template>
  <div class="financial-planner">
    <header class="app-header">
      <div class="header-content">
        <h1>Financial Planner</h1>
        <div class="dollar-toggle">
          <label class="dropdown-label">
            <span class="toggle-text">Dollar Type:</span>
            <select v-model="dollarType" class="dollar-select">
              <option value="future">Future Dollars</option>
              <option value="today">Today's Dollars</option>
            </select>
          </label>
        </div>
      </div>
    </header>

    <!-- Single Unified Financial Planning Component -->
    <div class="planning-content">
      <div v-if="!dataLoaded" class="loading-message">
        Loading financial data...
      </div>
      <FinancialProjection 
        v-else
        :cash-flow-items="cashFlowItems"
        :net-worth-items="netWorthItems"
        :personal-info="personalInfo"
        :dollar-type="dollarType"
        :inflation-rate="inflationRate"
        @update-cash-flow-items="updateCashFlowItems"
        @update-net-worth-items="updateNetWorthItems"
        @update-inflation-rate="updateInflationRate"
        @update-personal-info="updatePersonalInfo"
      />
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import FinancialProjection from './FinancialProjection.vue';

export default {
  name: 'EntityPlanning',
  components: {
    FinancialProjection
  },
  props: {
    entityExternalId: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const currentYear = new Date().getFullYear();
    const dollarType = ref('future'); // 'today' or 'future'
    const inflationRate = ref(0.025); // Default 2.5% inflation

    // Function to toggle between today's and future dollars
    const toggleDollarType = () => {
      dollarType.value = dollarType.value === 'today' ? 'future' : 'today';
    };

    // Function to update inflation rate
    const updateInflationRate = (newRate) => {
      inflationRate.value = newRate;
    };

    // Function to calculate age from date of birth
    const calculateAge = (dateOfBirth, forYear) => {
      if (!dateOfBirth) return null;
      
      // Handle different date formats
      let birthYear;
      if (typeof dateOfBirth === 'string') {
        // Try parsing various date formats
        const date = new Date(dateOfBirth);
        if (!isNaN(date.getTime())) {
          birthYear = date.getFullYear();
        } else {
          // Try extracting year from string formats like "1990", "1990-01-01", etc.
          const yearMatch = dateOfBirth.match(/\b(19|20)\d{2}\b/);
          if (yearMatch) {
            birthYear = parseInt(yearMatch[0]);
          }
        }
      } else if (typeof dateOfBirth === 'number') {
        birthYear = dateOfBirth;
      }
      
      if (!birthYear || birthYear < 1900 || birthYear > currentYear) {
        return null;
      }
      
      return forYear - birthYear;
    };

    // Function to process personal information from dossier
    const processPersonalInfo = (dossierData) => {
      const personalInfo = {
        primary: {
          legalFirstName: null,
          dateOfBirth: null,
          targetRetirementAge: null,
          lifeExpectancy: null
        },
        spouse: {
          legalFirstName: null,
          dateOfBirth: null,
          targetRetirementAge: null,
          lifeExpectancy: null
        }
      };

      try {
        // Process primary person's information
        if (dossierData.personal_information) {
          const primary = dossierData.personal_information;
          personalInfo.primary.legalFirstName = primary.legal_first_name;
          personalInfo.primary.dateOfBirth = primary.date_of_birth;
          personalInfo.primary.targetRetirementAge = parseInt(primary.target_retirement_age) || null;
          personalInfo.primary.lifeExpectancy = parseInt(primary.life_expectancy) || null;
        }

        // Process spouse's information (if exists)
        if (dossierData.spouse_personal_information) {
          const spouse = dossierData.spouse_personal_information;
          personalInfo.spouse.legalFirstName = spouse.legal_first_name;
          personalInfo.spouse.dateOfBirth = spouse.date_of_birth;
          personalInfo.spouse.targetRetirementAge = parseInt(spouse.target_retirement_age) || null;
          personalInfo.spouse.lifeExpectancy = parseInt(spouse.life_expectancy) || null;
        }

      } catch (error) {
        console.error('Error processing personal information:', error);
      }

      return personalInfo;
    };

    // Function to convert frequency amounts to annual
    const convertToAnnual = (amount, frequency) => {
      if (!frequency) return amount;
      
      const freq = frequency.toLowerCase();
      
      if (freq.includes('month')) {
        return amount * 12;
      } else if (freq.includes('week')) {
        if (freq.includes('bi') || freq.includes('every other') || freq.includes('every 2')) {
          // Biweekly (26 times per year)
          return amount * 26;
        } else {
          // Weekly (52 times per year)
          return amount * 52;
        }
      } else if (freq.includes('day')) {
        if (freq.includes('work') || freq.includes('business')) {
          // Work days (roughly 250 per year)
          return amount * 250;
        } else {
          // Daily (365 times per year)
          return amount * 365;
        }
      } else if (freq.includes('quarter')) {
        return amount * 4;
      } else if (freq.includes('semi') && freq.includes('annual')) {
        return amount * 2;
      }
      
      // Default to annual if unrecognized frequency
      return amount;
    };

    // Function to load dossier data and process it
    const loadDossierData = async () => {
      try {
        const response = await fetch(`/api/v1/dossier/entity/${props.entityExternalId}/`);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const dossierData = await response.json();
        
        if (!dossierData.dossier?.dossier_data) {
          return;
        }
        
        // Process the dossier data into cash flow and net worth items
        const processedCashFlow = processDossierToCashFlow(dossierData.dossier.dossier_data);
        const processedNetWorth = processDossierToNetWorth(dossierData.dossier.dossier_data);
        const processedPersonalInfo = processPersonalInfo(dossierData.dossier.dossier_data);
        
        // Update the reactive data
        cashFlowItems.value = processedCashFlow;
        netWorthItems.value = processedNetWorth;
        personalInfo.value = processedPersonalInfo;
        dataLoaded.value = true;
        
      } catch (error) {
        console.error('Error loading dossier:', error);
      }
    };

    // Process dossier data into cash flow format
    const processDossierToCashFlow = (dossierData) => {
      const items = [];
      
      try {
        // Process income sources
        if (dossierData.income?.income_sources) {
          dossierData.income.income_sources.forEach((income) => {
            const amount = parseFloat(income.income_amount) || 0;
            const frequency = income.income_frequency || 'annual';
            
            // Convert to annual using the new helper function
            const annualAmount = convertToAnnual(amount, frequency);
            
            const item = {
              id: `income_${Date.now()}_${Math.random()}`,
              name: income.income_source || 'Income',
              amount: annualAmount,
              frequency: 'Annually', // Always set to annually after conversion
              start_year: parseInt(income.income_start_date) || currentYear,
              end_year: parseInt(income.income_end_date) || 2070,
              growth_rate: income.income_growth_rate || 0.025,
              type: 'income'
            };
            
            items.push(item);
          });
        }
        
        // Process expenses
        if (dossierData.expenses?.expenses) {
          dossierData.expenses.expenses.forEach((expense) => {
            const amount = parseFloat(expense.expense_amount) || 0;
            const frequency = expense.expense_frequency || 'annual';
            
            // Convert to annual using the new helper function
            const annualAmount = convertToAnnual(amount, frequency);
            
            const item = {
              id: `expense_${Date.now()}_${Math.random()}`,
              name: expense.expense_type || 'Expense',
              amount: annualAmount,
              frequency: 'Annually', // Always set to annually after conversion
              start_year: parseInt(expense.expense_start_date) || currentYear,
              end_year: parseInt(expense.expense_end_date) || 2070,
              growth_rate: expense.expense_growth_rate || 0.025,
              type: 'expense'
            };
            
            items.push(item);
          });
        }
        
      } catch (error) {
        console.error('Error processing cash flow data:', error);
      }
      
      return items;
    };

    // Process dossier data into net worth format
    const processDossierToNetWorth = (dossierData) => {
      const items = [];
      const currentYear = new Date().getFullYear();

      // Helper to generate a UUIDv4
      const makeId = () => `nw_${Date.now()}_${Math.random()}`;

      // Helper to push an item and auto‐assign a unique id
      function pushItem(obj) {
        items.push({ id: makeId(), ...obj });
      }

      // Process real estate assets
      if (dossierData.assets?.real_estate) {
        dossierData.assets.real_estate.forEach(property => {
          const value = parseFloat(property.property_value) || 0;
          pushItem({
            name: property.property_type || 'Real Estate',
            amount: value,
            growth_rate: 0.03,
            contribution_rate: 0,
            start_year: currentYear,
            end_year: null,
            type: 'asset',
            category: 'Real Estate'
          });
        });
      }

      // Process investment accounts
      if (dossierData.assets?.investment_accounts) {
        dossierData.assets.investment_accounts.forEach(account => {
          const value = parseFloat(account.account_value) || 0;
          pushItem({
            name: account.account_name || account.account_type || 'Investment Account',
            amount: value,
            growth_rate: 0.07,
            contribution_rate: 0,
            start_year: currentYear,
            end_year: null,
            type: 'asset',
            category: 'Investments'
          });
        });
      }

      // Process bank accounts
      if (dossierData.assets?.bank_accounts) {
        dossierData.assets.bank_accounts.forEach(account => {
          const value = parseFloat(account.account_value) || 0;
          pushItem({
            name: account.account_title || 'Bank Account',
            amount: value,
            growth_rate: 0.01, // Lower growth for cash
            contribution_rate: 0,
            start_year: currentYear,
            end_year: null,
            type: 'asset',
            category: 'Cash & Bank'
          });
        });
      }

      // Process other assets
      if (dossierData.assets?.other_assets) {
        dossierData.assets.other_assets.forEach(asset => {
          const value = parseFloat(asset.asset_value) || 0;
          pushItem({
            name: asset.asset_name || asset.asset_type || 'Other Asset',
            amount: value,
            growth_rate: 0.02,
            contribution_rate: 0,
            start_year: currentYear,
            end_year: null,
            type: 'asset',
            category: 'Other Assets'
          });
        });
      }

      // Process liabilities
      if (dossierData.liabilities) {
        dossierData.liabilities.forEach(liability => {
          const balance = parseFloat(liability.liability_current_balance) || 0;
          const interestRate = parseFloat(
            (liability.liability_interest_rate || '').replace('%', '')
          ) / 100 || 0.04;
          const estimatedPayment = balance * 0.08;
          pushItem({
            name: liability.liability_description || liability.liability_type || 'Liability',
            amount: balance,
            growth_rate: interestRate,
            contribution_rate: -estimatedPayment,
            start_year: currentYear,
            end_year: currentYear + 25,
            type: 'liability'
          });
        });
      }

      return items;
    };

    // Load data when component mounts
    onMounted(() => {
      loadDossierData();
    });

    // Initialize with empty arrays - will be populated from dossier
    const cashFlowItems = ref([]);
    const netWorthItems = ref([]);
    const personalInfo = ref({
      primary: {
        legalFirstName: null,
        dateOfBirth: null,
        targetRetirementAge: null,
        lifeExpectancy: null
      },
      spouse: {
        legalFirstName: null,
        dateOfBirth: null,
        targetRetirementAge: null,
        lifeExpectancy: null
      }
    });
    const dataLoaded = ref(false);

    const updateCashFlowItems = (items) => {
      cashFlowItems.value = items;
    };

    const updateNetWorthItems = (items) => {
      netWorthItems.value = items;
    };
    const updatePersonalInfo = (updatedInfo) => {
      console.log('Parent received personal info update:', updatedInfo);
      personalInfo.value = updatedInfo;
    };

    return {
      cashFlowItems,
      netWorthItems,
      personalInfo,
      dataLoaded,
      dollarType,
      inflationRate,
      toggleDollarType,
      updateInflationRate,
      updateCashFlowItems,
      updateNetWorthItems,
      updatePersonalInfo,
    };
  }
};
</script>

<style>
/* FinancialPlanningStyles.css - Shared styles for financial planning components */

/* ========================================
   ENTITY PLANNING STYLES
   ======================================== */

.financial-planner {
  min-height: 100vh;
  background: #f8f9fa;
}

.app-header {
  background: white;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e0e0e0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.app-header h1 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.75rem;
  font-weight: 600;
}

/* Dollar Toggle Styles */
.dollar-toggle {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
}

.toggle-text {
  font-size: 0.9rem;
  font-weight: 500;
  color: #495057;
  min-width: 110px;
  text-align: right;
}

.toggle-button {
  position: relative;
  width: 56px;
  height: 28px;
  background: #e9ecef;
  border: none;
  border-radius: 14px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  padding: 0;
}

.toggle-button.future-dollars { background: #007bff; }

.toggle-indicator {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 22px;
  height: 22px;
  background: white;
  border-radius: 50%;
  transition: transform 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.toggle-button.future-dollars .toggle-indicator { transform: translateX(28px); }

.toggle-info {
  display: flex;
  align-items: center;
  color: #6c757d;
  cursor: help;
}

.toggle-info svg { width: 18px; height: 18px; }

.planning-content {
  background: #f8f9fa;
  min-height: calc(100vh - 90px);
}

.loading-message {
  padding: 2rem;
  text-align: center;
  background: white;
  margin: 2rem;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  color: #6c757d;
}

/* ========================================
   FINANCIAL PROJECTION STYLES
   ======================================== */

.financial-projection { padding: 1rem; }

.financial-projection h2 {
  color: #2c3e50;
  margin-bottom: 1.5rem;
}

/* Inflation Notice (blue) */
.inflation-notice {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #e3f2fd;
  border: 1px solid #90caf9;
  border-radius: 6px;
  margin-bottom: 1.5rem;
  color: #1565c0;
  font-size: 0.9rem;
}

/* Charts Section */
.charts-section { margin-bottom: 3rem; }

.chart-type-select {
  padding: 0.5rem;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 0.85rem;
  background: white;
  cursor: pointer;
}

.chart-type-select:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.chart-tabs {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  background: #f1f3f4;
  padding: 0.25rem;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.chart-tab {
  background: transparent;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  color: #6c757d;
  transition: all 0.2s ease;
  flex: 1;
}

.chart-tab:hover { background: #e9ecef; color: #495057; }

.chart-tab.active {
  background: white;
  color: #2c3e50;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  font-weight: 600;
}

.chart-content {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
}

/* Items Section */
.items-section {
  margin-bottom: 3rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.items-section-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e0e0e0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8f9fa;
  transition: background-color 0.2s ease;
}

.items-section-header:hover { background: #e9ecef; }

.items-section-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.3rem;
}

.items-summary { display: flex; align-items: center; gap: 1rem; }

.item-count {
  color: #6c757d;
  font-size: 0.9rem;
  font-weight: 500;
}

.collapse-toggle {
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.collapse-toggle:hover { background: #dee2e6; color: #495057; }

.collapse-toggle svg { transition: transform 0.2s ease; }

.collapse-toggle.expanded svg { transform: rotate(180deg); }

.items-content, .table-content { padding: 1.5rem; }

/* Personal Information Section */
.personal-info-section {
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 2px solid #e0e0e0;
}

.personal-info-section h3 {
  color: #2c3e50;
  margin-bottom: 0.75rem;
  font-size: 1.1rem;
}

.personal-assumptions-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}

.personal-info-side h4,
.assumptions-side h4 {
  color: #495057;
  margin-bottom: 0.75rem;
  font-size: 0.95rem;
  font-weight: 600;
}

.personal-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.add-spouse-card {
  background: #f8f9fa;
  border: 2px dashed #dee2e6;
  border-radius: 6px;
  padding: 0.75rem;
  text-align: center;
  transition: all 0.2s ease;
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.add-spouse-card:hover { border-color: #adb5bd; background: #e9ecef; }

.btn-add-spouse {
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  transition: all 0.2s ease;
}

.btn-add-spouse:hover { background: #0056b3; transform: translateY(-1px); }

/* Financial Items Section */
.financial-items-section h3 {
  color: #2c3e50;
  margin-bottom: 1rem;
  font-size: 1.2rem;
}

.four-column-layout {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
}

/* ========================================
   CHART STYLES (Shared)
   ======================================== */
.chart-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  margin: 2rem 0;
  width: 100%;
}
.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e0e0e0;
  flex-wrap: wrap;
  gap: 1rem;
}

.chart-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.25rem;
}

.chart-controls {
  display: flex;
  gap: 1.5rem;
  align-items: center;
}

.btn-toggle {
  background: #f8f9fa;
  color: #495057;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  transition: all 0.2s ease;
}

.btn-toggle:hover { background: #e9ecef; border-color: #adb5bd; }

.btn-toggle.active { background: #007bff; color: white; border-color: #007bff; }

.chart-wrapper { min-height: 400px; }

.dollar-select { margin-left: 8px; padding: 4px 8px; font-size: 14px; }

.dropdown-label { display: flex; align-items: center; gap: 8px; }

/* ========================================
   RESPONSIVE DESIGN
   ======================================== */

@media (max-width: 1200px) {
  .four-column-layout {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
  .personal-assumptions-grid { grid-template-columns: 1fr; gap: 1.5rem; }
  .personal-cards { grid-template-columns: 1fr; }
}

@media (max-width: 768px) {
  .header-content { flex-direction: column; gap: 1rem; align-items: stretch; }
  .dollar-toggle { justify-content: center; }
  .toggle-text { text-align: left; }

  .four-column-layout { grid-template-columns: 1fr; gap: 1rem; }
  .chart-tabs { flex-direction: column; }

  .chart-header { flex-direction: column; gap: 1rem; align-items: stretch; }
  .chart-controls { justify-content: center; }
  .personal-assumptions-grid { grid-template-columns: 1fr; gap: 1rem; }
  .personal-cards { grid-template-columns: 1fr; }
}

/* ========================================
   UTILITY CLASSES
   ======================================== */

.text-center { text-align: center; }

.mt-1 { margin-top: 0.25rem; }
.mt-2 { margin-top: 0.5rem; }
.mt-3 { margin-top: 1rem; }
.mt-4 { margin-top: 1.5rem; }
.mt-5 { margin-top: 3rem; }

.mb-1 { margin-bottom: 0.25rem; }
.mb-2 { margin-bottom: 0.5rem; }
.mb-3 { margin-bottom: 1rem; }
.mb-4 { margin-bottom: 1.5rem; }
.mb-5 { margin-bottom: 3rem; }

.p-1 { padding: 0.25rem; }
.p-2 { padding: 0.5rem; }
.p-3 { padding: 1rem; }
.p-4 { padding: 1.5rem; }
.p-5 { padding: 3rem; }

.gap-1 { gap: 0.25rem; }
.gap-2 { gap: 0.5rem; }
.gap-3 { gap: 1rem; }
.gap-4 { gap: 1.5rem; }
.gap-5 { gap: 3rem; }


/* ========================================
   MONTE CARLO CHART STYLES
   ======================================== */

.btn-simulate {
  background: #3498db;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.75rem 1.5rem;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.2s ease;
}

.chart-container .empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 500px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 2px dashed #dee2e6;
}

.chart-container .empty-state p {
  color: #6c757d;
  font-size: 1.1rem;
  font-style: italic;
}

.simulation-info {
  display: flex;
  gap: 1.5rem;
  align-items: center;
}

.chart-wrapper {
  min-height: 500px;
  position: relative;
  width: 100%;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 500px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 2px dashed #dee2e6;
}

.empty-state p {
  color: #6c757d;
  font-size: 1.1rem;
  font-style: italic;
}

.simulation-settings {
  display: flex;
  gap: 2rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e0e0e0;
  flex-wrap: wrap;
}

.setting-group label {
  color: #495057;
  font-size: 0.9rem;
  font-weight: 500;
}

.setting-input {
  padding: 0.5rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 0.9rem;
  background: white;
  min-width: 120px;
}

.setting-input:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.25);
}

@media (max-width: 768px) {
  .chart-header {
    flex-direction: column;
    align-items: stretch;
  }
  
  .chart-controls {
    flex-direction: column;
    align-items: stretch;
  }
  
  .simulation-settings {
    flex-direction: column;
    gap: 1rem;
  }
  
  .setting-group {
    justify-content: space-between;
  }
}


/* ========================================
   SANKEY CHART STYLES
   ======================================== */

.sankey-chart {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
}

.year-control {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  min-width: 200px;
}

.year-control label { font-weight: 500; color: #374151; font-size: 0.9rem; }

.year-slider {
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: #e5e7eb;
  outline: none;
  -webkit-appearance: none;
}

.year-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #3b82f6;
  cursor: pointer;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.year-slider::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #3b82f6;
  cursor: pointer;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.year-range {
  display: flex;
  justify-content: space-between;
  width: 100%;
  font-size: 0.8rem;
  color: #6b7280;
}

.sankey-chart .chart-container { min-height: 500px; position: relative; }

.sankey-chart .no-data-message {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 500px;
  color: #6b7280;
  font-size: 1.1rem;
}

.flow-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 2rem;
}

.summary-card {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  text-align: center;
  border-left: 4px solid;
}

.summary-card h4 {
  margin: 0 0 0.5rem 0;
  font-size: 0.9rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.summary-card .amount { margin: 0; font-size: 1.5rem; font-weight: 700; }

.income-summary { border-left-color: #10b981; }
.income-summary h4 { color: #059669; }
.income-summary .amount { color: #10b981; }

.expense-summary { border-left-color: #ef4444; }
.expense-summary h4 { color: #dc2626; }
.expense-summary .amount { color: #ef4444; }

.net-summary { border-left-color: #3b82f6; }
.net-summary h4 { color: #1d4ed8; }
.net-summary .amount { color: #3b82f6; }

.net-summary.negative { border-left-color: #ef4444; }
.net-summary.negative h4 { color: #dc2626; }
.net-summary.negative .amount { color: #ef4444; }

@media (max-width: 768px) {
  .year-control { align-items: stretch; }
}


/* ========================================
   ASSUMPTIONS CARD STYLES (scoped)
   ======================================== */

.assumptions-card {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  height: fit-content;
}

.assumptions-card:hover { box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08); }

.assumptions-card .card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f0f0f0;
}

.assumptions-card .card-header h5 {
  margin: 0;
  color: #2c3e50;
  font-size: 0.9rem;
  font-weight: 600;
}

.assumptions-card .edit-toggle {
  background: none;
  border: 1px solid #dee2e6;
  border-radius: 3px;
  padding: 0.25rem;
  cursor: pointer;
  color: #6c757d;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.assumptions-card .edit-toggle:hover { border-color: #007bff; color: #007bff; background: #f8f9ff; }
.assumptions-card .edit-toggle.editing { background: #28a745; border-color: #28a745; color: white; }

.assumptions-card .assumptions-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.25rem 0.75rem;
}

.assumptions-card .assumption-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.25rem 0;
}

.assumptions-card .assumption-label {
  font-weight: 500;
  color: #6c757d;
  font-size: 0.75rem;
}

.assumptions-card .assumption-value {
  color: #2c3e50;
  font-weight: 600;
  font-size: 0.8rem;
}

/* Edit Mode (scoped) */
.assumptions-card .edit-mode { font-size: 0.8rem; }

.assumptions-card .edit-sections {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.assumptions-card .edit-section h6 {
  margin: 0 0 0.5rem 0;
  color: #495057;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 0.25rem;
}

.assumptions-card .edit-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
}

.assumptions-card .edit-field { display: flex; flex-direction: column; gap: 0.125rem; }

.assumptions-card .edit-field label {
  font-size: 0.65rem;
  font-weight: 600;
  color: #495057;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.assumptions-card .field-input {
  padding: 0.375rem;
  border: 1px solid #ced4da;
  border-radius: 3px;
  font-size: 0.75rem;
  transition: all 0.2s ease;
}

.assumptions-card .field-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
}

.assumptions-card .edit-actions {
  display: flex;
  gap: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid #f0f0f0;
}

.assumptions-card .btn-done {
  background: #28a745;
  color: white;
  border: none;
  border-radius: 3px;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.8rem;
  transition: all 0.2s ease;
}

.assumptions-card .btn-done:hover { background: #218838; }

.assumptions-card .btn-reset {
  background: white;
  color: #6c757d;
  border: 1px solid #6c757d;
  border-radius: 3px;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.8rem;
  transition: all 0.2s ease;
}

.assumptions-card .btn-reset:hover { background: #6c757d; color: white; }

@media (max-width: 768px) {
  .assumptions-card .assumptions-grid { grid-template-columns: 1fr; }
  .assumptions-card .edit-grid { grid-template-columns: 1fr; }
}


/* === Financial Item Cards (scoped) === */

.compact-financial-item {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: white;
  transition: all 0.2s ease;
  cursor: pointer;
  overflow: hidden;
}

.compact-financial-item:hover { border-color: #c0c0c0; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05); }

/* Left border color based on item type */
.income-item { border-left: 3px solid #2ecc71; }
.expense-item { border-left: 3px solid #e74c3c; }
.asset-item { border-left: 3px solid #3498db; }
.liability-item { border-left: 3px solid #e67e22; }

/* Read Mode (scoped) */
.compact-financial-item .read-mode {
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  position: relative;
  font-size: 0.8rem;
}

.item-main { display: flex; justify-content: space-between; align-items: flex-start; gap: 0.5rem; }

.item-name {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
  flex: 1;
  line-height: 1.3;
}

.item-amount { font-weight: 700; color: #2c3e50; font-size: 0.95rem; white-space: nowrap; }

.item-details { display: flex; gap: 0.5rem; flex-wrap: wrap; align-items: center; }

.item-details > span {
  font-size: 0.75rem;
  color: #6c757d;
  background: #f8f9fa;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  white-space: nowrap;
}

.detail-years { font-weight: 500; }

/* Edit button (appears on hover) */
.edit-btn {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: none;
  border: none;
  color: #adb5bd;
  padding: 0.25rem;
  cursor: pointer;
  border-radius: 3px;
  transition: all 0.2s ease;
  opacity: 0;
}

.compact-financial-item:hover .edit-btn { opacity: 1; }
.edit-btn:hover { color: #495057; background: #f8f9fa; }

/* Edit Mode (scoped) */
.compact-financial-item .edit-mode {
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  cursor: default;
  background: #f8f9fa;
}

.edit-header { display: flex; gap: 0.5rem; align-items: center; }

.edit-name {
  flex: 1;
  padding: 0.4rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 600;
}

.edit-name:focus { outline: none; border-color: #007bff; box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25); }

.compact-financial-item .btn-done,
.compact-financial-item .btn-remove {
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: bold;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.compact-financial-item .btn-done { background: #28a745; color: white; }
.compact-financial-item .btn-done:hover { background: #218838; }

.compact-financial-item .btn-remove { background: #dc3545; color: white; }
.compact-financial-item .btn-remove:hover { background: #c82333; }

.edit-row { display: flex; gap: 0.5rem; }

.compact-financial-item .edit-field { flex: 1; display: flex; flex-direction: column; gap: 0.25rem; }

.compact-financial-item .edit-field label {
  font-size: 0.7rem;
  font-weight: 600;
  color: #495057;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.compact-financial-item .edit-field input,
.compact-financial-item .edit-field select {
  padding: 0.4rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.8rem;
  width: 100%;
  box-sizing: border-box;
}

.compact-financial-item .edit-field input:focus,
.compact-financial-item .edit-field select:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

/* === Compact Financial Section === */

.compact-financial-section {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e0e0e0;
}

.section-header h4 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.1rem;
  font-weight: 600;
}

.categories-wrapper { display: flex; flex-direction: column; gap: 1rem; flex: 1; }

.category-group {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 0.75rem;
}

.category-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #dee2e6;
}

.category-name { color: #495057; font-size: 0.9rem; font-weight: 600; }

.category-count {
  color: #6c757d;
  font-size: 0.8rem;
  background: #e9ecef;
  padding: 0.2rem 0.5rem;
  border-radius: 12px;
  min-width: 1.5rem;
  text-align: center;
}

.items-list { display: flex; flex-direction: column; gap: 0.5rem; flex: 1; }

.compact-financial-section .empty-state {
  text-align: center;
  padding: 2rem 1rem;
  color: #6c757d;
  background: #f8f9fa;
  border-radius: 6px;
  border: 2px dashed #dee2e6;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  flex: 1;
  justify-content: center;
}

.compact-financial-section .empty-state p { margin: 0; font-size: 0.9rem; }

/* === Add Button Styles === */
.btn-add,
.btn-add-empty {
  border: none;
  border-radius: 4px;
  padding: 0.4rem 0.8rem;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.btn-add-empty { padding: 0.6rem 1.2rem; }

/* Type-specific button colors */
.income-btn { background: #2ecc71; color: white; }
.income-btn:hover { background: #27ae60; transform: translateY(-1px); }

.expense-btn { background: #e74c3c; color: white; }
.expense-btn:hover { background: #c0392b; transform: translateY(-1px); }

.asset-btn { background: #3498db; color: white; }
.asset-btn:hover { background: #2980b9; transform: translateY(-1px); }

.liability-btn { background: #e67e22; color: white; }
.liability-btn:hover { background: #d35400; transform: translateY(-1px); }

/* Personal Info Card (scoped chrome + form) */

.add-spouse-card { margin-top: 1rem; }

.personal-info-card {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
}

.personal-info-card:hover { box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08); }

.personal-info-card .card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  padding-bottom: 0.375rem;
  border-bottom: 1px solid #f0f0f0;
}

.personal-info-card .card-header h5 {
  margin: 0;
  color: #2c3e50;
  font-size: 0.9rem;
  font-weight: 600;
}

.personal-info-card .edit-toggle {
  background: none;
  border: 1px solid #dee2e6;
  border-radius: 3px;
  padding: 0.25rem;
  cursor: pointer;
  color: #6c757d;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.personal-info-card .edit-toggle:hover { border-color: #007bff; color: #007bff; background: #f8f9ff; }
.personal-info-card .edit-toggle.editing { background: #28a745; border-color: #28a745; color: white; }

.info-grid { display: flex; flex-direction: column; gap: 0.25rem; }

.info-item { 
  display: flex; 
  justify-content: space-between; 
  align-items: center; 
  padding: 0.125rem 0; }


.info-item.retirement-info {
  background: #f8f9fa;
  padding: 0.375rem;
  border-radius: 3px;
  margin-top: 0.25rem;
}

.info-label { font-weight: 500; color: #6c757d; font-size: 0.75rem; }
.info-value { color: #2c3e50; font-weight: 600; font-size: 0.8rem; }

/* Edit Mode (scoped) */
.personal-info-card .edit-mode { font-size: 0.8rem; }

.personal-info-card .edit-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.personal-info-card .edit-field { display: flex; flex-direction: column; gap: 0.125rem; }

.personal-info-card .edit-field label {
  font-size: 0.65rem;
  font-weight: 600;
  color: #495057;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.personal-info-card .field-input {
  padding: 0.375rem;
  border: 1px solid #ced4da;
  border-radius: 3px;
  font-size: 0.75rem;
  transition: all 0.2s ease;
}

.personal-info-card .field-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
}

@media (max-width: 768px) {
  .personal-info-card .edit-grid { grid-template-columns: 1fr; }
}


/* ========================================
   UNIFIED TABLE
   ======================================== */

.unified-table-wrapper {
  width: 100%;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

/* Breadcrumb Navigation */
.breadcrumb-nav {
  padding: 1rem 1.5rem 0 1.5rem;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  color: #6c757d;
}

.breadcrumb-btn {
  background: none;
  border: none;
  color: #007bff;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.9rem;
  padding: 0;
}

.breadcrumb-btn:hover { color: #0056b3; }

.breadcrumb-separator { margin: 0 0.5rem; color: #adb5bd; }

.breadcrumb-current { color: #495057; font-weight: 500; }

/* Table Controls */
.table-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
}

.table-controls h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.25rem;
}

.back-btn {
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.back-btn:hover { background: #5a6268; transform: translateY(-1px); }

/* Table Container */
.table-container {
  overflow-x: auto;
  max-height: 70vh;
  overflow-y: auto;
}

/* Base Table Styling */
.financial-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;
}

.financial-table th,
.financial-table td {
  border: 1px solid #e0e0e0;
  padding: 0.75rem 0.5rem;
  text-align: right;
  white-space: nowrap;
  font-size: 0.9rem;
}

.financial-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #495057;
  position: sticky;
  top: 0;
  z-index: 10;
}

/* Year Column */
.year-header,
.year-cell {
  text-align: left !important;
  font-weight: 600;
  min-width: 80px;
  position: sticky;
  left: 0;
  background: #fff;
  z-index: 15;
  box-shadow: 2px 0 2px -1px rgba(0, 0, 0, 0.1);
}

.year-header { background: #f8f9fa !important; z-index: 20; }

/* Age Columns */
.age-cell {
  font-size: 0.85rem;
  font-weight: 500;
  text-align: center;
  background: #fafafa;
  color: #6c757d;
}

.age-header {
  background: #e9ecef !important;
  color: #495057 !important;
  font-size: 0.85rem;
}

/* Clickable Elements */
.clickable-header {
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.clickable-header:hover { background: #e9ecef !important; transform: scale(1.02); }

.clickable-cell { cursor: pointer; transition: all 0.2s ease; }

.clickable-cell:hover { background: #e3f2fd !important; font-weight: 700; }

/* Header Colors */
.assets-header { background: #e3f2fd; color: #1976d2; }
.liabilities-header { background: #fff3e0; color: #f57c00; }
.networth-header { background: #e8f5e9; color: #388e3c; font-weight: 700; }
.portfolio-header { background: #f3e5f5; color: #7b1fa2; }
.income-header { background: #e8f5e9; color: #2e7d32; }
.expenses-header { background: #ffebee; color: #d32f2f; }
.savings-header { background: #e1f5fe; color: #0277bd; }
.withdrawals-header { background: #fff8e1; color: #f57f17; }
.shortfall-header { background: #ffebee; color: #c62828; }
.total-header {
  background: #f0f0f0;
  color: #495057;
  font-weight: 700;
  position: sticky;
  right: 0;
  box-shadow: -2px 0 2px -1px rgba(0, 0, 0, 0.1);
}

/* Cell Colors */
.summary-cell { font-weight: 600; min-width: 120px; }
.networth-cell { background: #f8fcff; color: #1976d2; }
.savings-cell { background: #faf5fb; color: #7b1fa2; }

/* Net Savings Conditional Formatting */
.positive-cash-flow { background: #e8f5e9 !important; color: #2e7d32 !important; font-weight: 700; }
.negative-cash-flow { background: #ffebee !important; color: #d32f2f !important; font-weight: 700; }

.detail-cell { background: #fafafa; min-width: 100px; }

.total-cell {
  background: #f8f9fa;
  color: #495057;
  font-weight: 700;
  position: sticky;
  right: 0;
  box-shadow: -2px 0 2px -1px rgba(0, 0, 0, 0.1);
}

/* Special States */
.negative { color: #d32f2f !important; font-weight: 700; }
.has-shortfall { background: #ffcdd2 !important; color: #b71c1c !important; font-weight: 700; }

/* Hover Effects */
.data-row:hover { background-color: rgba(0, 123, 255, 0.02); }
.data-row:hover .summary-cell,
.data-row:hover .detail-cell { filter: brightness(0.98); }

/* Responsive for table */
@media (max-width: 768px) {
  .table-controls { flex-direction: column; gap: 1rem; align-items: stretch; }
  .breadcrumb-nav { padding: 0.75rem 1rem; }
  .financial-table { font-size: 0.8rem; min-width: 600px; }
  .financial-table th, .financial-table td { padding: 0.5rem 0.25rem; }
}

/* Scrollbar Styling */
.table-container::-webkit-scrollbar { height: 8px; width: 8px; }
.table-container::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
.table-container::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 4px; }
.table-container::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }



/* ========================================
   SIMPLIFIED ROTH CONVERSION STYLES
   ======================================== */

.decision-summary {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.decision-summary h3 {
  margin: 0 0 20px 0;
  color: #2c3e50;
  font-size: 20px;
}

.quick-decision {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 15px;
}

.decision-good {
  background: #f8fff9;
  border: 2px solid #28a745;
}

.decision-maybe {
  background: #fffbf0;
  border: 2px solid #ffc107;
}

.decision-bad {
  background: #fff5f5;
  border: 2px solid #dc3545;
}

.decision-result {
  display: flex;
  align-items: center;
  gap: 15px;
}

.decision-icon {
  font-size: 32px;
}

.decision-result h4 {
  margin: 0 0 5px 0;
  font-size: 18px;
  color: #2c3e50;
}

.decision-result p {
  margin: 0;
  color: #495057;
  line-height: 1.4;
}

.key-metric {
  text-align: center;
  background: white;
  padding: 15px;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.metric-label {
  font-size: 12px;
  color: #6c757d;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 5px;
}

.metric-value {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
}

.explanation-box {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 6px;
  border-left: 4px solid #007bff;
}

.explanation-box p {
  margin: 0;
  color: #495057;
  line-height: 1.5;
}

/* Simple Configuration */
.simple-config {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.simple-config h4 {
  margin: 0 0 15px 0;
  color: #2c3e50;
  font-size: 16px;
}

.config-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.config-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.config-item label {
  font-size: 14px;
  font-weight: 600;
  color: #495057;
}

.config-input {
  padding: 10px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 16px;
}

.config-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0,123,255,.25);
}

/* Tax Comparison */
.tax-comparison {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 6px;
}

.tax-rate {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}

.tax-label {
  font-size: 12px;
  color: #6c757d;
  font-weight: 600;
  text-align: center;
}

.tax-value {
  font-size: 20px;
  font-weight: 700;
  padding: 8px 12px;
  border-radius: 4px;
}

.tax-value.current {
  background: #e3f2fd;
  color: #1976d2;
}

.tax-value.retirement {
  background: #f3e5f5;
  color: #7b1fa2;
}

.vs {
  font-size: 16px;
  font-weight: 600;
  color: #6c757d;
}

/* Results Summary */
.results-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.result-item {
  background: white;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
}

.result-item.positive {
  border-color: #28a745;
  background: #f8fff9;
}

.result-item.negative {
  border-color: #dc3545;
  background: #fff5f5;
}

.result-item.good {
  border-color: #28a745;
}

.result-item.ok {
  border-color: #ffc107;
}

.result-item.bad {
  border-color: #dc3545;
}

.result-item h5 {
  margin: 0 0 10px 0;
  font-size: 14px;
  color: #6c757d;
  font-weight: 600;
  text-transform: uppercase;
}

.result-amount {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 8px;
}

.result-item.positive .result-amount {
  color: #28a745;
}

.result-item.negative .result-amount {
  color: #dc3545;
}

.result-item small {
  color: #6c757d;
  font-size: 12px;
  line-height: 1.3;
}

/* Chart Section */
.chart-section {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.chart-section .chart-header {
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 15px;
  margin-bottom: 20px;
}

.chart-section h4 {
  margin: 0;
  color: #2c3e50;
  font-size: 16px;
}

.chart-explanation {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 15px;
}

.chart-explanation p {
  margin: 0;
  font-size: 14px;
  color: #495057;
}

/* Action Section */
.action-section {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
}

.action-section h4 {
  margin: 0 0 15px 0;
  color: #2c3e50;
  font-size: 16px;
}

.action-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.action-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
}

.action-number {
  background: #007bff;
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  flex-shrink: 0;
}

.action-text {
  line-height: 1.4;
  color: #495057;
}

/* Responsive */
@media (max-width: 768px) {
  .quick-decision {
    flex-direction: column;
    gap: 15px;
  }
  
  .config-grid {
    grid-template-columns: 1fr;
  }
  
  .tax-comparison {
    flex-direction: column;
    gap: 10px;
  }
  
  .results-summary {
    grid-template-columns: 1fr;
  }
}
</style>
